import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Constante } from '../../constante';
import { ProductoConfigPayload, ProductoDetalleApi, ProductoListApi, ProductoListQuery, ProductosWebPayload, ProductosWebResultApi, ProductoWebPayload, ProductoWebValidacionApi } from '../models/api/producto-api';
import { PagedResultApi, ResponseApi } from '../models/response-api';
import { AppUtil } from '../utility/app-util';

@Injectable({ providedIn: 'root' })
export class ProductoService {
    private http = inject(HttpClient);
    private url = AppUtil.addTrailingSlash(environment.URL_API_PEDIDOS) + 'api/producto/';

    listProductos(query: ProductoListQuery = {}): Observable<PagedResultApi<ProductoListApi>> {
        let params = new HttpParams()
            .set('pageNumber', String(query.pageNumber ?? 1))
            .set('pageSize', String(query.pageSize ?? Constante.PAGINATION_SIZE));

        if (query.categoriaId != null) {
            params = params.set('categoriaId', String(query.categoriaId));
        }
        if (query.precioDesde != null) {
            params = params.set('precioDesde', String(query.precioDesde));
        }
        if (query.precioHasta != null) {
            params = params.set('precioHasta', String(query.precioHasta));
        }
        if (query.orden) {
            params = params.set('orden', query.orden);
        }
        if (query.key) {
            params = params.set('key', query.key);
        }
        if (query.tipo) {
            params = params.set('tipo', query.tipo);
        }
        if (query.activado != null) {
            params = params.set('activado', String(query.activado));
        }
        if (query.incluirOcultos) {
            params = params.set('incluirOcultos', 'true');
        }

        return this.http.get<ResponseApi<PagedResultApi<ProductoListApi>>>(this.url + 'list-productos', { params }).pipe(
            map((res) => {
                if (res.success === Constante.REQUEST_STATUS_OK && res.data) {
                    return res.data;
                }
                return { total: 0, page: 1, pageSize: query.pageSize ?? Constante.PAGINATION_SIZE, data: [] };
            })
        );
    }

    listMasVendidos(): Observable<ProductoListApi[]> {
        return this.http.get<ResponseApi<ProductoListApi[]>>(this.url + 'list-mas-vendidos').pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && Array.isArray(res.data) ? res.data : []))
        );
    }

    getProducto(id: string): Observable<ProductoDetalleApi | null> {
        return this.http.get<ResponseApi<ProductoDetalleApi>>(this.url + 'get-producto/' + encodeURIComponent(id)).pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && res.data ? res.data : null))
        );
    }

    validarProductoWeb(body: ProductoWebPayload): Observable<ProductoWebValidacionApi | null> {
        return this.http.get<ResponseApi<ProductoWebValidacionApi>>(this.url + 'validar-producto-web', { params: { tipo: body.tipo, id: body.id } }).pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && res.data ? res.data : null))
        );
    }

    activarProducto(body: ProductoWebPayload): Observable<boolean> {
        return this.http.post<ResponseApi<unknown>>(this.url + 'activar-producto', body).pipe(
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }

    desactivarProducto(body: ProductoWebPayload): Observable<boolean> {
        return this.http.post<ResponseApi<unknown>>(this.url + 'desactivar-producto', body).pipe(
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }

    activarProductos(items: ProductosWebPayload[]): Observable<ProductosWebResultApi | null> {
        return this.http.post<ResponseApi<ProductosWebResultApi>>(this.url + 'activar-productos', { items }).pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && res.data ? res.data : null))
        );
    }

    desactivarProductos(items: ProductosWebPayload[]): Observable<ProductosWebResultApi | null> {
        return this.http.post<ResponseApi<ProductosWebResultApi>>(this.url + 'desactivar-productos', { items }).pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && res.data ? res.data : null))
        );
    }

    configProducto(body: ProductoConfigPayload): Observable<boolean> {
        return this.http.post<ResponseApi<unknown>>(this.url + 'config-producto', body).pipe(
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }
}
